// ColumnSetEditor.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import "./columnSetEditor.scss";
import { ButtonControl, DialogControl, IconControl, SidePanelFormControl } from "spark-core-dx/components";

/* ---------- Utils ---------- */

const KEY_PREFIX = "ColumnSet:";
const EMPTY_SOURCE_OBJECT = {};
const VERSION_SUFFIX_RE = /\s*\(Version [^)]+\)$/i;
function stripVersionSuffix(name) {
  if (!name) return name;
  let prev, next = String(name);
  do { prev = next; next = next.replace(VERSION_SUFFIX_RE, ""); } while (next !== prev);
  return next;
}

function makeKey(sourceObject) {
  if (!sourceObject) return null;
  const id =
    sourceObject.SourceObjectId ||
    sourceObject._id ||
    sourceObject.UniqueId ||
    sourceObject.SourceObjectID;
  return id ? `${KEY_PREFIX}${id}` : null;
}

function readFieldsFromSession(sourceObject) {
  const key = makeKey(sourceObject);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.Fields) ? parsed.Fields : null;
  } catch {
    return null;
  }
}

function writeFieldsToSession(sourceObject, fields) {
  const key = makeKey(sourceObject);
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ Fields: fields }));
  } catch {}
}

function comparableFields(fields) {
  return JSON.stringify((Array.isArray(fields) ? fields : []).map((field) => normalizeField(field)));
}

function comparableSourceObject(sourceObject) {
  const { Fields, fields, ...rest } = sourceObject ?? {};
  return JSON.stringify(rest ?? {});
}

function clearSessionForSourceObject(sourceObject) {
  const key = makeKey(sourceObject);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {}
}

function safe(s) { return s ?? ""; }
function getFieldHeader(field) {
  return (
    field?.DisplayName ||
    field?.FieldName ||
    field?.SystemName ||
    field?.ObjectFieldName ||
    ""
  );
}
function getFieldTypeBadge(field) {
  return field?.SystemDataType || field?.DataTypeName || field?.DataTypeId || "";
}

const getOperationKey = (operation) =>
  operation?.Name || operation?.OperationTypeId || operation?.Value || operation?._id || operation?.Id || "";

const getOperationLabel = (operation) =>
  operation?.DisplayName || operation?.Text || getOperationKey(operation);

// --- Boolean/number normalization helpers ---
const asBool = (v) =>
  v === true || v === 1 || v === "1" || (typeof v === "string" && v.toLowerCase() === "true");

const normalizeField = (f) => {
  if (!f) return f;
  const next = { ...f };
  if ("IsActive" in next)      next.IsActive = asBool(next.IsActive);
  if ("IsDisabled" in next)    next.IsDisabled = asBool(next.IsDisabled);
  if ("GridSequence" in next)  next.GridSequence = Number(next.GridSequence) || 0;
  return next;
};

/* ---------- Reusable card ---------- */

function SectionCard({ title, subtitle, right, children }) {
  return (
    <div className="ps-card">
      {(title || right || subtitle) && (
        <div className="ps-card-header">
          <div>
            {title && <div className="ps-card-title">{title}</div>}
            {subtitle && <div className="ps-card-subtitle">{subtitle}</div>}
          </div>
          {right && <div>{right}</div>}
        </div>
      )}
      <div className="ps-card-body">{children}</div>
    </div>
  );
}

/**
 * ColumnSetEditor
 *
 * Props:
 * - sourceObject
 * - refresh
 * - onSave(updatedSourceObject)
 * - onChange(updatedSourceObject)
 * - onEditField?(field)
 * - fieldItemMetadata
 * - sourceObjectItemMetadata
 */
export function ColumnSetEditor({
  sourceObject,
  refresh,
  onSave,
  onChange,
  onEditField,
  fieldItemMetadata,
  sourceObjectItemMetadata,
  sourceObjectOperations = [],
  onSourceObjectOperationClick,
}) {
  const so = sourceObject ?? EMPTY_SOURCE_OBJECT;
  const soId =
    so.SourceObjectId || so._id || so.UniqueId || so.SourceObjectID || null;

  // Initial fields: prefer sessionStorage, then inbound
  const initialFields = (() => {
    const saved = readFieldsFromSession(so);
    if (saved) return saved.map(normalizeField);
    return Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
  })();

  const [fields, setFields] = useState(initialFields);
  const [sourceDraft, setSourceDraft] = useState(so);
  const [undoFields, setUndoFields] = useState(null);
  const [canUndo, setCanUndo] = useState(false);

  // Side panel state for editing a single field
  const [openEditSidePanel, setOpenEditSidePanel] = useState(false);
  const [editItemData, setEditItemData] = useState(null);
  const [openSourceObjectSidePanel, setOpenSourceObjectSidePanel] = useState(false);
  const [sourceObjectEditData, setSourceObjectEditData] = useState(null);
  const [operationMenuOpen, setOperationMenuOpen] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const operationMenuRef = useRef(null);

  const [searchActive, setSearchActive] = useState("");
  const [searchAvailable, setSearchAvailable] = useState("");
  const [leftSort, setLeftSort] = useState("grid"); // 'grid' | 'name'
  const [rightSort, setRightSort] = useState("name"); // 'name' | 'type'
  const hasSourceObjectOperations = Array.isArray(sourceObjectOperations);

  /* ---------- Keep onChange in sync ---------- */
  useEffect(() => {
    if (typeof onChange === "function") {
      try {
        onChange({ ...(sourceDraft ?? so ?? {}), Fields: fields });
      } catch {}
    }
  }, [fields, sourceDraft, so, onChange]);

  /* ---------- Reload when sourceObject or refresh changes ---------- */
  useEffect(() => {
    if (refresh && typeof refresh === "object" && Array.isArray(refresh.Fields)) {
      const inbound = refresh.Fields.map(normalizeField);
      setFields(inbound);
      setSourceDraft({ ...refresh, Fields: inbound });
      writeFieldsToSession(so, inbound);
    } else if (readFieldsFromSession(so)) {
      const saved = readFieldsFromSession(so);
      setSourceDraft(so);
      setFields(saved.map(normalizeField));
    } else {
      const inbound = Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
      setSourceDraft(so);
      setFields(inbound);
      writeFieldsToSession(so, inbound);
    }
    setUndoFields(null);
    setCanUndo(false);
  }, [soId, refresh, so]);

  useEffect(() => {
    if (!operationMenuOpen) return undefined;

    const handleDocumentMouseDown = (event) => {
      if (!operationMenuRef.current || operationMenuRef.current.contains(event.target)) return;
      setOperationMenuOpen(false);
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [operationMenuOpen]);

  /* ---------- Derived lists (ONLY IsActive controls visibility) ---------- */
  const isActive = (f) => asBool(f?.IsActive);

  const activeColumns = useMemo(
    () => fields.filter(isActive),
    [fields]
  );
  const inactiveColumns = useMemo(
    () => fields.filter((f) => !isActive(f)),
    [fields]
  );

  const hasUnsavedChanges = useMemo(() => {
    const savedFields = Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
    return (
      comparableFields(fields) !== comparableFields(savedFields) ||
      comparableSourceObject(sourceDraft) !== comparableSourceObject(so)
    );
  }, [fields, so, sourceDraft]);

  const sourceObjectFormFields = useMemo(
    () =>
      (Array.isArray(sourceObjectItemMetadata?.Fields) ? sourceObjectItemMetadata.Fields : []).filter((field) => {
        const fieldName = String(field?.FieldName ?? field?.Name ?? "").toLowerCase();
        return fieldName !== "fields";
      }),
    [sourceObjectItemMetadata]
  );

  const sortedActive = useMemo(() => {
    const arr = [...activeColumns];
    arr.sort((a, b) => {
      if (leftSort === "grid") {
        const ga = a?.GridSequence ?? a?.Sequence ?? 0;
        const gb = b?.GridSequence ?? b?.Sequence ?? 0;
        if (ga !== gb) return ga - gb;
      }
      const ha = getFieldHeader(a);
      const hb = getFieldHeader(b);
      return ha.localeCompare(hb);
    });
    const q = searchActive.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((f) => {
      const hay = (
        getFieldHeader(f) +
        " " +
        safe(f.Description) +
        " " +
        safe(f.SystemName)
      ).toLowerCase();
      return hay.includes(q);
    });
  }, [activeColumns, leftSort, searchActive]);

  const sortedInactive = useMemo(() => {
    const arr = [...inactiveColumns];
    arr.sort((a, b) => {
      if (rightSort === "type") {
        const ta = getFieldTypeBadge(a);
        const tb = getFieldTypeBadge(b);
        if (ta !== tb) return ta.localeCompare(tb);
      }
      const ha = getFieldHeader(a);
      const hb = getFieldHeader(b);
      return ha.localeCompare(hb);
    });
    const q = searchAvailable.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((f) => {
      const hay = (
        getFieldHeader(f) +
        " " +
        safe(f.Description) +
        " " +
        safe(f.SystemName)
      ).toLowerCase();
      return hay.includes(q);
    });
  }, [inactiveColumns, rightSort, searchAvailable]);

  /* ---------- Undo helpers ---------- */
  const clearUndo = () => { setUndoFields(null); setCanUndo(false); };
  const invalidateUndoOnEdit = () => { clearUndo(); };

  /* ---------- Field mutators (use _id; normalize) ---------- */
  function updateFields(updateFn) {
    setFields((prev) => {
      const next = typeof updateFn === "function" ? updateFn(prev) : updateFn;
      writeFieldsToSession(so, next);
      return next;
    });
  }
  function updateField(fieldId, patch) {
    if (!fieldId) return;
    invalidateUndoOnEdit();
    updateFields((prev) =>
      prev.map((f) => (f?._id === fieldId ? normalizeField({ ...f, ...patch }) : f))
    );
  }
  function activateColumn(field) {
    if (!field) return;
    const id = field?._id;
    invalidateUndoOnEdit();
    const maxGridSeq = fields.reduce((max, f) => {
      const gs = typeof f?.GridSequence === "number" ? f.GridSequence : 0;
      return gs > max ? gs : max;
    }, 0);
    updateField(id, {
      IsActive: true,
      IsDisabled: false,
      GridSequence: maxGridSeq + 1,
    });
  }
  function deactivateColumn(field) {
    if (!field) return;
    const id = field?._id;
    invalidateUndoOnEdit();
    updateField(id, { IsActive: false });
  }

  // OPEN side panel on pencil click
  function handleEditFieldClick(field) {
    if (typeof onEditField === "function") {
      try { onEditField(field); } catch {}
    }
    setEditItemData({ ...(field ?? {}) });
    setOpenEditSidePanel(true);
  }

  function handleChangeFieldValue(field, key, value) {
    const id = field?._id;
    updateField(id, { [key]: value });
  }

  /* ---------- Save / Rollback / Clear ---------- */
  const handleSave = async () => {
    if (!hasUnsavedChanges) return { Success: true };
    setShowSaveConfirm(true);
    return { Success: true };
  };

  const handleConfirmSave = async () => {
    const updated = { ...(sourceDraft ?? so ?? {}), Fields: fields };
    setSavePending(true);
    if (onSave) {
      try {
        const result = await onSave(updated);
        if (result?.Success) {
          setUndoFields(null);
          setCanUndo(false);
          setShowSaveConfirm(false);
        }
        return result;
      } finally {
        setSavePending(false);
      }
    }

    setSavePending(false);
    setShowSaveConfirm(false);
    return { Success: true };
  };

  const handleRollbackOrUndo = () => {
    if (canUndo && undoFields) {
      setFields(undoFields);
      writeFieldsToSession(so, undoFields);
      clearUndo();
    } else {
      setUndoFields(fields);
      setCanUndo(true);
      clearSessionForSourceObject(so);
      const inbound = Array.isArray(so.Fields) ? so.Fields.map(normalizeField) : [];
      setFields(inbound);
      writeFieldsToSession(so, inbound);
      setSourceDraft(so);
    }
  };
  const handleHardClear = () => {
    clearSessionForSourceObject(so);
    const cleared = fields.map((f) => normalizeField({ ...f, IsActive: false }));
    setFields(cleared);
    setUndoFields(null);
    setCanUndo(false);
  };

  const handleEditSourceObjectClick = () => {
    setSourceObjectEditData({ ...(sourceDraft ?? so ?? {}), Fields: fields });
    setOpenSourceObjectSidePanel(true);
  };

  /* ---------- SidePanelFormControl handlers (field edit) ---------- */

  const handleFieldPanelCancel = () => {
    setOpenEditSidePanel(false);
    setEditItemData(null);
  };

  const handleFieldPanelSave = async (updatedField) => {
    const id = updatedField?._id ?? editItemData?._id;
    if (!id) {
      handleFieldPanelCancel();
      return { Success: true, Items: [updatedField] };
    }

    const normalized = normalizeField(updatedField);

    updateFields((prev) =>
      prev.map((f) => (f?._id === id ? { ...f, ...normalized } : f))
    );

    setOpenEditSidePanel(false);
    setEditItemData(null);

    return { Success: true, Items: [normalized] };
  };

  const handleSourceObjectPanelCancel = () => {
    setOpenSourceObjectSidePanel(false);
    setSourceObjectEditData(null);
  };

  const handleSourceObjectPanelSave = async (updatedSourceObject) => {
    setSourceDraft((previous) => ({
      ...(previous || so || {}),
      ...(updatedSourceObject || {}),
      Fields: fields,
    }));
    setOpenSourceObjectSidePanel(false);
    setSourceObjectEditData(null);
    return { Success: true, Items: [updatedSourceObject] };
  };

  const handleOperationClick = (operation) => {
    setOperationMenuOpen(false);
    if (typeof onSourceObjectOperationClick === "function") {
      onSourceObjectOperationClick(operation);
    }
  };

  /* ---------- Row components ---------- */

  const ActiveColumnRow = useMemo(() => {
  return function ActiveColumnRow({ field, onEdit, onDeactivate, onChangeValue }) {
    const header = getFieldHeader(field);
    const typeBadge = getFieldTypeBadge(field);

    const displayName = field?.DisplayName ?? "";
    const description = field?.Description ?? "";
    const aiContext   = field?.AIPromptContext ?? "";

    return (
      <div className="ps-card ps-entry-card cs-column-card">
        <div className="ps-entry-header">
          <div className="ps-entry-left">
            <div className="ps-entry-top">
              <IconControl
                iconKey="edit"
                className="cs-edit-icon-btn"
                title="Open advanced field editor"
                onClick={() => onEdit(field)}
              />
              <div className="ps-name" title={header}>{header}</div>
              {typeBadge && <span className="ps-badge">{typeBadge}</span>}
            </div>
            {field?.FieldName && (
              <div className="ps-desc-line" title={field.FieldName}>
                {field.FieldName}
              </div>
            )}
          </div>
          <div className="ps-entry-right">
            <button
              type="button"
              onClick={() => onDeactivate(field)}
              className="ps-btn"
              title="Remove from active columns"
            >
              Remove
            </button>
          </div>
        </div>

        <div className="cs-edit-row">
          <div className="cs-edit-fields">
            <label className="cs-edit-field">
              <span className="cs-edit-label">Display Name</span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => onChangeValue(field, "DisplayName", e.target.value)}
                className="cs-input"
              />
            </label>

            <label className="cs-edit-field">
              <span className="cs-edit-label">Description</span>
              <textarea
                rows={1}
                value={description}
                onChange={(e) => onChangeValue(field, "Description", e.target.value)}
                className="cs-textarea"
              />
            </label>

            <label className="cs-edit-field">
              <span className="cs-edit-label">AI Prompt Context</span>
              <textarea
                rows={1}
                value={aiContext}
                onChange={(e) => onChangeValue(field, "AIPromptContext", e.target.value)}
                className="cs-textarea"
              />
            </label>
          </div>
        </div>
      </div>
    );
  };
}, []); 

const InactiveColumnRow = useMemo(() => {
  return function InactiveColumnRow({ field, onActivate }) {
    const header = getFieldHeader(field);
    const typeBadge = getFieldTypeBadge(field);
    return (
      <li className="ps-item">
        <div className="ps-item-left">
          <div className="ps-item-top">
            <div className="ps-dname" title={header}>{header}</div>
            {typeBadge && <span className="ps-badge">{typeBadge}</span>}
          </div>
          {field?.Description && (
            <div className="ps-item-desc" title={field.Description}>
              {field.Description}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onActivate(field)}
          className="ps-item-add"
          title="Add to active columns"
        >
          Add
        </button>
      </li>
    );
  };
}, []); 

  /* ---------- Render ---------- */

  const baseTitle =
    stripVersionSuffix(
      sourceDraft?.DisplayName || sourceDraft?.SystemName || sourceDraft?.Name || so.DisplayName || so.SystemName || so.Name || "Column Set Editor"
    );
  const title = (sourceDraft?.DisplayVersion || so.DisplayVersion)
    ? `${baseTitle} (Version ${sourceDraft?.DisplayVersion || so.DisplayVersion})`
    : baseTitle;

  const desc =
    sourceDraft?.Description ||
    so.Description ||
    "Manage which fields/columns are active on the grid and edit their metadata.";

  return (
    <div className="column-set-editor">
      <div className="ps-root">
        {/* Header */}
        <div className="ps-header" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="ps-header-left">
            <div className="ps-title">{title}</div>
            {desc && <div className="ps-desc">{desc}</div>}
          </div>

          <div className="ps-actions" style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <ButtonControl type="standard" onClick={handleRollbackOrUndo}
              title={canUndo ? "Undo last Rollback (restore the previous working copy)"
                             : "Rollback to the server state (you can Undo this)"}>
              {canUndo ? "Undo Rollback" : "Rollback"}
            </ButtonControl>
            <ButtonControl type="ok" onClick={handleSave} disabled={!hasUnsavedChanges} title="Save column configuration">
              Save
            </ButtonControl>
            <ButtonControl type="cancel" onClick={handleHardClear}
              title="Clear active columns: mark all fields inactive and wipe session buffer">
              Clear
            </ButtonControl>
            <div className="cs-toolbar-spacer" />
            <button type="button" className="cs-toolbar-icon-btn" title="Edit Source Object" onClick={handleEditSourceObjectClick}>
              <IconControl iconKey={"edit"} />
            </button>
            {hasSourceObjectOperations && (
              <div className="cs-operation-menu" ref={operationMenuRef}>
                <button
                  type="button"
                  className="cs-toolbar-icon-btn"
                  title="Source Object Actions"
                  aria-label="Source object actions"
                  aria-haspopup="menu"
                  aria-expanded={operationMenuOpen}
                  onClick={() => setOperationMenuOpen((value) => !value)}
                >
                  <IconControl iconKey={"viewmore"} />
                </button>
                {operationMenuOpen && (
                  <div className="cs-operation-list" role="menu">
                    {sourceObjectOperations.length > 0 ? (
                      sourceObjectOperations.map((operation) => (
                        <button
                          type="button"
                          role="menuitem"
                          key={getOperationKey(operation)}
                          className="cs-operation-item"
                          onClick={() => handleOperationClick(operation)}
                        >
                          {getOperationLabel(operation)}
                        </button>
                      ))
                    ) : (
                      <div className="cs-operation-empty">No source actions available</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Two-pane layout */}
        <div className="ps-grid">
          {/* Left: Active Columns */}
          <div className="ps-card-left">
            <SectionCard
              title={`Active Columns (${activeColumns.length})`}
              subtitle="Columns currently shown on the grid."
              right={
                <div className="ps-controls">
                  <select
                    value={leftSort}
                    onChange={(e) => setLeftSort(e.target.value === "name" ? "name" : "grid")}
                    className="ps-select"
                  >
                    <option value="grid">Sort: Grid Sequence</option>
                    <option value="name">Sort: Name</option>
                  </select>
                  <input
                    type="text"
                    value={searchActive}
                    onChange={(e) => setSearchActive(e.target.value)}
                    placeholder="Search active columns..."
                    className="ps-input"
                  />
                </div>
              }
            >
              {sortedActive.length === 0 ? (
                <div className="ps-empty">No active columns. Add columns from the right.</div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {sortedActive.map((f) => (
                    <ActiveColumnRow
                      key={f._id}
                      field={f}
                      onEdit={handleEditFieldClick}
                      onDeactivate={deactivateColumn}
                      onChangeValue={handleChangeFieldValue}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          {/* Right: Inactive Columns */}
          <div className="ps-card-right">
            <SectionCard
              title={`Available Columns (${inactiveColumns.length})`}
              subtitle="Columns not currently shown on the grid."
              right={
                <div className="ps-controls">
                  <select
                    value={rightSort}
                    onChange={(e) => setRightSort(e.target.value === "type" ? "type" : "name")}
                    className="ps-select"
                  >
                    <option value="name">Sort: Name</option>
                    <option value="type">Sort: Type</option>
                  </select>
                  <input
                    type="text"
                    value={searchAvailable}
                    onChange={(e) => setSearchAvailable(e.target.value)}
                    placeholder="Search available..."
                    className="ps-input"
                  />
                </div>
              }
            >
              {sortedInactive.length === 0 ? (
                <div className="ps-empty">All fields are active or none match the search.</div>
              ) : (
                <div className="ps-available-scroll">
                  <ul className="ps-list">
                    {sortedInactive.map((f) => (
                      <InactiveColumnRow
                        key={`${f._id}-inactive`}
                        field={f}
                        onActivate={activateColumn}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      </div>

      <SidePanelFormControl
        key={
          (editItemData?._id ?? editItemData?.UniqueId) ||
          (openEditSidePanel ? "open" : "closed")
        }
        open={openEditSidePanel}
        title={editItemData?.DisplayName ? `Edit: ${editItemData.DisplayName}` : "Edit Column"}

        /* what SidePanelFormControl expects */
        metadata={fieldItemMetadata}
        formFields={Array.isArray(fieldItemMetadata?.Fields) ? fieldItemMetadata.Fields : []}
        itemData={editItemData}

        /* lifecycle */
        onSave={handleFieldPanelSave}
        onCancel={handleFieldPanelCancel}
        onAnimationEnd={(e) => {
          if (editItemData && openEditSidePanel && e.animationName === "sidePanelExit") {
            setEditItemData(null);
          }
        }}

        /* SidePanel will call this during width changes */
        onUpdateSidePanel={() => {}}

        /* state setters used internally by SidePanelFormControl */
        setOpenEditSidePanel={setOpenEditSidePanel}
        setItemData={setEditItemData}

        viewOnly={false}
      />

      {sourceObjectItemMetadata ? (
        <SidePanelFormControl
          key={
            (sourceObjectEditData?._id ?? sourceObjectEditData?.UniqueId ?? soId ?? "source-object-edit") +
            (openSourceObjectSidePanel ? "-open" : "-closed")
          }
          open={openSourceObjectSidePanel}
          title={sourceDraft?.DisplayName ? `Edit: ${sourceDraft.DisplayName}` : "Edit Source Object"}
          metadata={sourceObjectItemMetadata}
          formFields={sourceObjectFormFields}
          itemData={sourceObjectEditData}
          onSave={handleSourceObjectPanelSave}
          onCancel={handleSourceObjectPanelCancel}
          onAnimationEnd={(e) => {
            if (sourceObjectEditData && openSourceObjectSidePanel && e.animationName === "sidePanelExit") {
              setSourceObjectEditData(null);
            }
          }}
          onUpdateSidePanel={() => {}}
          setOpenEditSidePanel={(value) => {
            setOpenSourceObjectSidePanel(value);
            if (!value) setSourceObjectEditData(null);
          }}
          setItemData={setSourceObjectEditData}
          viewOnly={false}
        />
      ) : null}

      <DialogControl
        openDialog={showSaveConfirm}
        title={"Confirm Save"}
        onX={() => setShowSaveConfirm(false)}
        onCancel={() => setShowSaveConfirm(false)}
        onOk={handleConfirmSave}
        cancelText={"Cancel"}
        okText={savePending ? "Saving..." : "Save Changes"}
        disableOk={savePending}
      >
        <div className="cs-confirm-dialog">
          Any mappings may be affected if this source object configuration is saved.
        </div>
      </DialogControl>
    </div>
  );
}

export default ColumnSetEditor;
